import { logWarn } from 'lib/logging'
import { WorkflowTriggerEvent, type WorkflowTriggerFilterInput } from 'app/generated/workflows'
import type { Condition, FieldOperator } from './conditions'

// Categories are the ADA names in lib/cdtCodeDefaults.
export type TreatmentMilestone = `presented` | `booked` | `broken_booking` | `completed` | `closed`

export const TREATMENT_MILESTONES: readonly { value: TreatmentMilestone; label: string; summary: string }[] = [
  { value: `presented`, label: `Treatment presented`, summary: `New treatment is added to a patient's plan` },
  { value: `booked`, label: `Treatment booked`, summary: `Planned treatment gets an appointment` },
  {
    value: `broken_booking`,
    label: `Broken booking`,
    summary: `An appointment for planned treatment is broken or cancelled`,
  },
  { value: `completed`, label: `Treatment completed`, summary: `Planned treatment is completed` },
  { value: `closed`, label: `Treatment plan closed`, summary: `A plan is closed, marked inactive, or declined` },
]

export type TreatmentTriggerData = { milestone?: TreatmentMilestone; categories?: string[]; codes?: string[] }

/** The engine event each moment compiles to, and the extra change filters it needs. */
export const TREATMENT_MOMENT_EVENTS: Readonly<
  Record<TreatmentMilestone, { event: WorkflowTriggerEvent; filters: WorkflowTriggerFilterInput[] }>
> = {
  presented: { event: WorkflowTriggerEvent.TreatmentPlanProcedurePresented, filters: [] },
  booked: { event: WorkflowTriggerEvent.TreatmentPlanProcedureBooked, filters: [] },
  broken_booking: { event: WorkflowTriggerEvent.TreatmentPlanProcedureBrokenBooking, filters: [] },
  completed: { event: WorkflowTriggerEvent.TreatmentPlanProcedureCompleted, filters: [] },
  closed: { event: WorkflowTriggerEvent.TreatmentPlanClosed, filters: [] },
}

const PLAN_UPDATED = WorkflowTriggerEvent.TreatmentPlanUpdated

/** The moment an engine event stands for, in either case (stored docs lower-case it). */
export function treatmentMilestoneForEvent(event: string | undefined): TreatmentMilestone | undefined {
  const upper = event?.toUpperCase()
  if (upper === PLAN_UPDATED) return undefined
  return (Object.keys(TREATMENT_MOMENT_EVENTS) as TreatmentMilestone[]).find(
    (moment) => TREATMENT_MOMENT_EVENTS[moment].event === upper,
  )
}

// Drafts saved before the per-moment events put the moment in this filter on the plan update.
const LEGACY_MILESTONE_ATTRIBUTE = `treatmentMilestone`
const LIST_ATTRIBUTES = { categories: `cdtCategories`, codes: `procedureCodes` } as const

const TREATMENT_FILTER_ATTRIBUTES: readonly string[] = [LEGACY_MILESTONE_ATTRIBUTE, ...Object.values(LIST_ATTRIBUTES)]

/** Categories and codes describe only the lines that changed. On a list attribute the engine's
 *  `exactly` already means "includes", and `in` means "includes any of". */
export function treatmentFilters(treatment?: TreatmentTriggerData): WorkflowTriggerFilterInput[] {
  if (!treatment?.milestone) return []
  const filters: WorkflowTriggerFilterInput[] = [...TREATMENT_MOMENT_EVENTS[treatment.milestone].filters]
  for (const key of [`categories`, `codes`] as const) {
    const values = treatment[key]
    if (!values?.length) continue
    const attribute = LIST_ATTRIBUTES[key]
    filters.push(
      values.length > 1
        ? { attribute, operator: `in`, value: JSON.stringify(values) }
        : { attribute, operator: `exactly`, value: values[0] },
    )
  }
  return filters
}

// The API stores an `in` value as a real array; a draft built here holds it as JSON text.
type EngineFilter = { attribute: string; operator: string; value?: unknown }

function readList(value: unknown): string[] | undefined {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value !== `string`) return undefined
  try {
    const parsed: unknown = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.map(String) : undefined
  } catch {
    logWarn(`Unreadable treatment trigger list`, value)
    return undefined
  }
}

/** The moment comes from the engine event. On the plan update it is the legacy `treatmentMilestone`
 *  filter, or the legacy closed filter (`state` exactly CLOSED) that drafts saved that way carry. */
export function treatmentFromFilters<F extends EngineFilter>(
  filters: readonly F[],
  event?: string,
): { treatment?: TreatmentTriggerData; rest: F[] } {
  const own = filters.filter((filter) => TREATMENT_FILTER_ATTRIBUTES.includes(filter.attribute))
  let rest = filters.filter((filter) => !TREATMENT_FILTER_ATTRIBUTES.includes(filter.attribute))
  const legacy = own.find((filter) => filter.attribute === LEGACY_MILESTONE_ATTRIBUTE)?.value
  const onPlanUpdate = event?.toUpperCase() === PLAN_UPDATED
  let milestone: string | undefined = treatmentMilestoneForEvent(event)
  if (!milestone && (onPlanUpdate || !event) && typeof legacy === `string`) milestone = legacy
  const closedIndex = rest.findIndex(
    (filter) => filter.attribute === `state` && filter.operator === `exactly` && filter.value === `CLOSED`,
  )
  if (!milestone && onPlanUpdate && closedIndex >= 0) {
    milestone = `closed`
    rest = rest.filter((_, position) => position !== closedIndex)
  }
  if (!milestone) return { treatment: undefined, rest }
  const listOf = (attribute: string): string[] | undefined => {
    const filter = own.find((candidate) => candidate.attribute === attribute)
    if (!filter?.value) return undefined
    if (filter.operator === `in`) return readList(filter.value)
    return typeof filter.value === `string` ? [filter.value] : undefined
  }
  return {
    treatment: {
      milestone: milestone as TreatmentMilestone,
      categories: listOf(LIST_ATTRIBUTES.categories),
      codes: listOf(LIST_ATTRIBUTES.codes),
    },
    rest,
  }
}

/** The answer to a guided question is the one change row it owns; no row means "Any". */
export function guidedValue(conditions: readonly Condition[], field: string): string | undefined {
  const row = conditions.find(
    (condition) => condition.type === `field` && condition.field === field && condition.scope === `change`,
  )
  return row?.type === `field` ? row.value : undefined
}

export function withGuidedValue(
  conditions: readonly Condition[],
  field: string,
  operator: FieldOperator,
  value: string | undefined,
): Condition[] {
  const others = conditions.filter(
    (condition) => !(condition.type === `field` && condition.field === field && condition.scope === `change`),
  )
  const answer = value?.trim()
  return answer ? [...others, { type: `field`, field, operator, value: answer, scope: `change` }] : others
}

const GUIDED_FIELD_BY_MILESTONE: Partial<Record<TreatmentMilestone, string>> = {
  presented: `changedValue`,
  broken_booking: `unbookedValueAfter`,
  completed: `pendingLinesAfter`,
}
const GUIDED_FIELDS = Object.values(GUIDED_FIELD_BY_MILESTONE)

/** Switching moment drops the guided answers the new moment does not ask about, so a stale
 *  "pending lines is 0" cannot ride along unseen. Rows the author added themselves stay. */
export function withoutOtherMomentRows(conditions: readonly Condition[], milestone: TreatmentMilestone): Condition[] {
  const keep = GUIDED_FIELD_BY_MILESTONE[milestone]
  return conditions.filter(
    (condition) =>
      !(
        condition.type === `field` &&
        condition.scope === `change` &&
        GUIDED_FIELDS.some((field) => field !== keep && condition.field === field)
      ),
  )
}
